<script lang="ts" setup>
import { computed, ref } from 'vue';
import useModal, { ModalTypes } from '../../composables/useModal';
import Modal from '../Modal.vue';
import { useLocale } from '../../composables/useLocale';
import { Conflict, Exists } from './types';
import { Prettify } from '../../types';
import { UserToCreate } from '../NewUser/useCreateMultipleUsers';
import { CustomerMgmtTypes } from '@ilteducation/customer-mgmt';
import { CompleteEvent } from './index.vue';
import AlreadyExists from './AlreadyExists.vue';

const { t } = useLocale();

type Props = {
  name: ModalTypes;
  conflicts: Conflict[];
  currentConflict: Exists;
};
const props = defineProps<Props>();
const emits = defineEmits<{
  (event: 'resolveCurrentConflict', value: CompleteEvent): void;
}>();

const { isOpen, close } = useModal(props.name);

const conflictsResolved = ref(false);
const removeUsers = ref<UserToCreate[]>([]);
const useInstead = ref<Record<string, Prettify<CustomerMgmtTypes.GetOrganizationUsersResponse['data'][number]>>>({});

const reset = () => {
  conflictsResolved.value = false;
  currentComponentRef.value = null;
  removeUsers.value = [];
  useInstead.value = {};
};

const onUpdate = (
  key: string,
  user: Prettify<CustomerMgmtTypes.GetOrganizationUsersResponse['data'][number]> | null,
) => {
  if (user) {
    useInstead.value[key] = user;
    return;
  }

  delete useInstead.value[key];
};

const isReadyToResolve = computed(() => {
  return useInstead.value && Object.keys(useInstead.value).length > 0;
});

const resolveCurrentConflict = () => {
  emits('resolveCurrentConflict', {
    useInstead: useInstead.value,
    resolvedKeys: [props.currentConflict.user.key],
    removeUsers: [],
    propertyComplete: {},
  });

  close();
  reset();
};

const onCreateNewUser = () => {
  emits('resolveCurrentConflict', {
    resolvedKeys: [props.currentConflict.user.key],
    removeUsers: [],
    useInstead: {},
    propertyComplete: {},
  });

  close();
  reset();
};

const currentComponentRef = ref();
</script>
<template>
  <Modal
    :name="name"
    :is-open="isOpen"
    :title="t('lcp.user_already_exists', { name: currentConflict ? currentConflict.user.name : '' })"
    :actionLabel="t('lcp.done')"
    :action="resolveCurrentConflict"
    :action-disabled="!isReadyToResolve"
    show-cancel-action
    :cancel-action-label="t('lcp.create_new')"
    :cancel-action="onCreateNewUser"
    :cancel-disabled="isReadyToResolve"
    @close="reset"
  >
    <AlreadyExists
      v-if="currentConflict && currentConflict.type === 'exists'"
      ref="currentComponentRef"
      :users="currentConflict.users"
      :user="currentConflict.user"
      :key="currentConflict.keys"
      @update="onUpdate"
      :disabled="false"
      hide-title
    />
  </Modal>
</template>
